// src/components/timer/FullScreenTimer.tsx
// Modo foco: usa Fullscreen API para esconder abas do navegador

import React, { useEffect, useRef } from 'react';
import { Minimize2, Square } from 'lucide-react';
import { useStudyTimer } from '../../contexts/StudyTimerContext';
import { getTypeByValue } from '../planner/constants/studyTypes';
import './FullScreenTimer.css';

const fmt = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const FullScreenTimer: React.FC = () => {
    const { activeTimer, isFullScreen, closeFullScreen, openStopModal } = useStudyTimer();
    const overlayRef = useRef<HTMLDivElement>(null);

    // ── Entra/sai do fullscreen nativo do navegador ──────────────────────
    useEffect(() => {
        if (!isFullScreen) return;

        const el = overlayRef.current;
        if (!el) return;

        const requestFS = () => {
            if (el.requestFullscreen) {
                el.requestFullscreen().catch(() => { });
            } else if ((el as any).webkitRequestFullscreen) {
                (el as any).webkitRequestFullscreen();
            }
        };

        requestFS();

        const onFSChange = () => {
            const fsEl =
                document.fullscreenElement ||
                (document as any).webkitFullscreenElement;
            if (!fsEl) {
                closeFullScreen();
            }
        };

        document.addEventListener('fullscreenchange', onFSChange);
        document.addEventListener('webkitfullscreenchange', onFSChange);

        return () => {
            document.removeEventListener('fullscreenchange', onFSChange);
            document.removeEventListener('webkitfullscreenchange', onFSChange);
        };
    }, [isFullScreen, closeFullScreen]);

    // ── Ao fechar pelo botão, sai do fullscreen nativo também ────────────
    const handleMinimize = () => {
        if (document.fullscreenElement || (document as any).webkitFullscreenElement) {
            if (document.exitFullscreen) {
                document.exitFullscreen().catch(() => { });
            } else if ((document as any).webkitExitFullscreen) {
                (document as any).webkitExitFullscreen();
            }
        }
        closeFullScreen();
    };

    if (!isFullScreen || !activeTimer) return null;

    const typeInfo = getTypeByValue(activeTimer.type);
    const typeLabel = activeTimer.type === 'other'
        ? (activeTimer.customType || 'Outro')
        : typeInfo.label;

    return (
        <div ref={overlayRef} className="fst-overlay">
            <button className="fst-btn-minimize" onClick={handleMinimize} title="Minimizar">
                <Minimize2 size={22} />
            </button>

            <div className="fst-body">
                <p className="fst-type-label">{typeLabel}</p>
                {activeTimer.subject && (
                    <p className="fst-subject-label">{activeTimer.subject}</p>
                )}
                <div className="fst-time">{fmt(activeTimer.elapsed)}</div>

                <button className="fst-btn-stop" onClick={openStopModal}>
                    <Square size={18} fill="currentColor" />
                    Parar sessão
                </button>
            </div>
        </div>
    );
};

export default FullScreenTimer;